import { useSelector, useDispatch } from 'react-redux'
import { useEffect } from 'react'
import { AppDispatch, RootState } from '@/redux/store'
import { GameRoomStatus, RoleChoice, Room, Status } from '@/redux/types/room'
import { fetchTeamsByRoomId } from '@/redux/asyncThunks/team'

import OGEAvatar from '../OGE/OGEAvatar'
import { SERVER_URL } from '@/api/config'
import { useTeamName } from '@/hooks/useTeamName'

const PlayerTeamDisplay = ({ teamId }: { teamId: number }) => {
  const teamName = useTeamName(teamId)
  return <span className='block text-xs text-gray-500'>Team: {teamName}</span>
}

interface RoomSummaryProps {
  isPolling: boolean
  currentRoom: Room
}

const RoomSummary: React.FC<RoomSummaryProps> = ({
  isPolling,
  currentRoom,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { roomMembers } = useSelector((state: RootState) => state.room)

  useEffect(() => {
    if (currentRoom?.roomid) {
      dispatch(fetchTeamsByRoomId(currentRoom.roomid))
    }
  }, [dispatch, currentRoom?.roomid])

  const acceptedPlayers = roomMembers.filter(
    (member) =>
      member.invitation_status === Status.Accepted &&
      member.role === RoleChoice.PLAYER
  )
  const hasGameSpecificKeys =
    !!currentRoom?.openai_api_key_configured &&
    !!currentRoom?.claude_api_key_configured

  return (
    <div>
      {currentRoom?.status === GameRoomStatus.NOT_STARTED && (
        <div className='mt-6'>
          <div className='flex items-center gap-2'>
            <h5 className='text-xl font-bold text-dark-blue'>Waiting Room</h5>
            {isPolling && (
              <span className='flex items-center gap-1 text-sm text-green-600'>
                <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                Auto-refreshing
              </span>
            )}
          </div>
          {acceptedPlayers.length === 0 ? (
            <div className='mt-2 text-sm text-gray-500'>
              No player has accepted the game room invite yet.
              {isPolling && (
                <span className='ml-1 text-green-600'>
                  Waiting for players to join...
                </span>
              )}
            </div>
          ) : (
            <div className='mt-2 space-y-2'>
              {acceptedPlayers.map((player) => (
                <div
                  className='flex items-center gap-4 py-1 transition-all duration-200'
                  key={player.gamealias}
                >
                  <OGEAvatar
                    src={
                      player.member_profile_picture
                        ? SERVER_URL + player.member_profile_picture
                        : null
                    }
                    fallback={player.member_name?.substring(0, 2).toUpperCase()}
                    variant='md'
                    isLoading={false}
                  />
                  <span className='text-sm'>
                    {player.member_name?.split('(')[0].trim() ||
                      player.member_email}
                    {player.teamId ? (
                      <PlayerTeamDisplay teamId={player.teamId} />
                    ) : (
                      <span className='block text-xs text-gray-500'>
                        No team assigned yet
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className='mt-6'>
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm'>
          <div className='mb-4'>
            <span className='text-xl font-bold text-dark-blue'>Summary</span>
          </div>
          <div className='grid grid-cols-2 md:grid-cols-3 gap-3 text-sm'>
            <div className='bg-white px-3 py-2 rounded-lg border border-gray-200'>
              <span className='font-medium text-gray-600'>Events:</span>
              <p className='font-semibold text-dark-blue'>
                {currentRoom?.events?.length || 0}
              </p>
            </div>
            <div className='bg-white px-3 py-2 rounded-lg border border-gray-200'>
              <span className='font-medium text-gray-600'>Advisors:</span>
              <p className='font-semibold text-dark-blue'>
                {currentRoom?.advisors_count || 0}
              </p>
            </div>
            <div className='bg-white px-3 py-2 rounded-lg border border-gray-200'>
              <span className='font-medium text-gray-600'>Players:</span>
              <p className='font-semibold text-dark-blue'>
                {currentRoom?.players_count || 0}
              </p>
            </div>
            <div className='bg-white px-3 py-2 rounded-lg border border-gray-200'>
              <span className='font-medium text-gray-600'>Scenario:</span>
              <p className='font-semibold text-dark-blue truncate'>
                {currentRoom?.scenario_title || 'Not configured'}
              </p>
            </div>
            <div className='bg-white px-3 py-2 rounded-lg border border-gray-200'>
              <span className='font-medium text-gray-600'>Mode:</span>
              <p className='font-semibold text-dark-blue'>
                {currentRoom?.game_mode || 'Not set'}
              </p>
            </div>
            <div className='bg-white px-3 py-2 rounded-lg border border-gray-200 col-span-2 md:col-span-1'>
              <span className='font-medium text-gray-600'>LLM Key Source:</span>
              <p
                className={`font-semibold ${
                  hasGameSpecificKeys ? 'text-emerald-700' : 'text-blue-700'
                }`}
              >
                {hasGameSpecificKeys
                  ? 'User-defined keys'
                  : 'Default server keys'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoomSummary
